import * as React from "react";
import { getOrCreateSessionId } from "../../ApiUtils/session";
import { loadHistory, saveHistory, clearHistory } from "../../ApiUtils/history";
import { createChatbotPortalWebSocket, getApplicationPermissions } from "./../../ApiUtils/ApiUtils";
import { getReportId, subscribe } from "../../store/reportStore";
import "./ChatbotFloatingWidget.css";

type Props = {
    title?: string;
    icon?: React.ReactNode;
    iconUrl?: string;
    iconSize?: number;
    showIconInHeader?: boolean;
    showIconOnToggle?: boolean;
};

type ChatMessage = {
    id: string;
    isUser: boolean;
    text: string;
    timestamp: number;
    agentName?: string;
    isStreaming?: boolean;
};

type WsInbound =
    | { type: "status"; message?: string }
    | { type: "chunk"; content?: string }
    | { type: "complete"; response?: string }
    | { type: "error"; error?: string }
    | Record<string, any>;

function makeId(prefix: string) {
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const ChatbotFloatingWidget: React.FC<Props> = (props) => {
    const title = props.title || "AI Assistant";

    const showIconInHeader =
        typeof props.showIconInHeader === "boolean" ? props.showIconInHeader : true;
    const showIconOnToggle =
        typeof props.showIconOnToggle === "boolean" ? props.showIconOnToggle : true;
    const iconSize = typeof props.iconSize === "number" ? props.iconSize : 22;

    const hasCustomIcon = !!props.icon || !!props.iconUrl;

    const sessionId = React.useMemo(() => getOrCreateSessionId(), []);
    const [messages, setMessages] = React.useState<ChatMessage[]>(() =>
        (loadHistory(sessionId) as any[]).map((m: any) => ({
            id: m.id || makeId(m.isUser ? "u" : "a"),
            isUser: !!m.isUser,
            text: String(m.text || ""),
            timestamp: Number(m.timestamp || Date.now()),
            agentName: m.agentName,
            isStreaming: false,
        }))
    );

    const [input, setInput] = React.useState<string>("");
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [reportId, setReportIdState] = React.useState<string | null>(() => getReportId());
    const [open, setOpen] = React.useState<boolean>(false);

    const [connStatus, setConnStatus] = React.useState<"Connected" | "Connecting" | "Disconnected">("Connecting");
    const [streamStatus, setStreamStatus] = React.useState<string>("");

    const [permissionLoaded, setPermissionLoaded] = React.useState(false);
    const [hasAiChatbotPermission, setHasAiChatbotPermission] = React.useState(false);

    const listRef = React.useRef<HTMLDivElement>(null);

    const wsRef = React.useRef<WebSocket | null>(null);
    const reconnectTimerRef = React.useRef<number | null>(null);
    const unmountedRef = React.useRef<boolean>(false);

    const currentAssistantIdRef = React.useRef<string | null>(null);
    const currentHtmlRef = React.useRef<string>("");
    const reconnectAttemptRef = React.useRef<number>(0);

    const wsConnected = connStatus === "Connected";

    React.useEffect(() => {
        saveHistory(sessionId, messages as any);
    }, [messages, sessionId]);

    React.useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo({
                top: listRef.current.scrollHeight,
                behavior: "smooth" as ScrollBehavior,
            });
        }
    }, [messages, isSending]);

    React.useEffect(() => {
        const unsub = subscribe(function (id) {
            setReportIdState(id);
        });
        setReportIdState(getReportId());
        return unsub;
    }, []);

    React.useEffect(() => {
        let cancelled = false;

        async function checkPermissions() {
            try {
                const response = await getApplicationPermissions();
                const permissionsArray = response?.applications ?? [];

                let allowed = false;

                permissionsArray.forEach((perm: any) => {
                    if (
                        perm.name === "Chat@prod" ||
                        perm.displayName?.toLowerCase().includes("chatbot")
                    ) {
                        allowed = true;
                    }
                });

                if (!cancelled) {
                    setHasAiChatbotPermission(allowed);
                    setPermissionLoaded(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAiChatbotPermission(false);
                    setPermissionLoaded(true);
                }
            }
        }

        checkPermissions();

        return () => {
            cancelled = true;
        };
    }, []);

    function renderIcon(sz: number) {
        if (props.icon) {
            return (
                <span className="chatbot-icon-box" style={{ ["--icon-size" as any]: `${sz}px` }}>
                    {props.icon}
                </span>
            );
        }
        if (props.iconUrl) {
            return (
                <span className="chatbot-icon-box" style={{ ["--icon-size" as any]: `${sz}px` }}>
                    <img src={props.iconUrl} alt="chatbot icon" className="chatbot-icon-img" />
                </span>
            );
        }
        return null;
    }

    function scheduleReconnect() {
        if (reconnectTimerRef.current) return;

        reconnectAttemptRef.current += 1;
        const delay = Math.min(60000, 5000 * Math.pow(2, reconnectAttemptRef.current - 1));

        reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            if (!unmountedRef.current && hasAiChatbotPermission) connectWs();
        }, delay);
    }

    function connectWs() {
        try {
            setConnStatus("Connecting");
            const ws = createChatbotPortalWebSocket();
            wsRef.current = ws;

            ws.onopen = () => {
                setConnStatus("Connected");
                reconnectAttemptRef.current = 0;
            };

            ws.onmessage = (event) => {
                let data: WsInbound;
                try {
                    data = JSON.parse(String(event.data));
                } catch {
                    data = { type: "chunk", content: String(event.data) };
                }
                handleInbound(data);
            };

            ws.onerror = () => {
                setConnStatus("Disconnected");
            };

            ws.onclose = () => {
                setConnStatus("Disconnected");
                scheduleReconnect();
            };
        } catch {
            setConnStatus("Disconnected");
            scheduleReconnect();
        }
    }

    React.useEffect(() => {
        // wait for permission result
        if (!permissionLoaded || !hasAiChatbotPermission) return;

        unmountedRef.current = false;
        connectWs();

        return () => {
            unmountedRef.current = true;

            if (reconnectTimerRef.current) {
                window.clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            try {
                wsRef.current?.close();
            } catch {
            }
            wsRef.current = null;
        };
    }, [permissionLoaded, hasAiChatbotPermission]);

    function ensureStreamingAssistant() {
        if (currentAssistantIdRef.current) return currentAssistantIdRef.current;

        const id = makeId("a");
        currentAssistantIdRef.current = id;
        currentHtmlRef.current = "";

        setMessages((prev) =>
            prev.concat({
                id,
                isUser: false,
                text: "",
                timestamp: Date.now(),
                agentName: "AI Assistant",
                isStreaming: true,
            })
        );

        return id;
    }

    function updateAssistant(id: string, html: string, isStreaming: boolean) {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: html, isStreaming } : m)));
    }

    function endStream(finalHtml: string) {
        const id = currentAssistantIdRef.current || ensureStreamingAssistant();
        updateAssistant(id, finalHtml, false);

        currentAssistantIdRef.current = null;
        currentHtmlRef.current = "";
        setStreamStatus("");
        setIsSending(false);
    }

    function handleInbound(data: WsInbound) {
        const type = (data as any)?.type;

        if (type === "status") {
            setStreamStatus(typeof (data as any).message === "string" ? (data as any).message : "");
            return;
        }

        if (type === "chunk") {
            const chunk = typeof (data as any).content === "string" ? (data as any).content : "";
            const id = ensureStreamingAssistant();
            currentHtmlRef.current = (currentHtmlRef.current || "") + chunk;
            updateAssistant(id, currentHtmlRef.current, true);
            return;
        }

        if (type === "complete") {
            const full =
                typeof (data as any).response === "string" && (data as any).response
                    ? (data as any).response
                    : currentHtmlRef.current || "";
            endStream(full);
            return;
        }

        if (type === "error") {
            const errText =
                typeof (data as any).error === "string" && (data as any).error
                    ? (data as any).error
                    : "An error occurred.";
            endStream(errText);
            return;
        }

        const fallback =
            typeof (data as any)?.content === "string"
                ? (data as any).content
                : typeof (data as any)?.response === "string"
                    ? (data as any).response
                    : "";

        if (fallback) {
            const id = ensureStreamingAssistant();
            currentHtmlRef.current = (currentHtmlRef.current || "") + fallback;
            updateAssistant(id, currentHtmlRef.current, true);
        }
    }

    async function send() {
        const text = (input || "").trim();
        if (!text || isSending) return;

        if (!reportId) {
            setMessages((prev) =>
                prev.concat({
                    id: makeId("a"),
                    isUser: false,
                    text: "ReportId is not available yet.",
                    timestamp: Date.now(),
                    agentName: "AI Assistant",
                })
            );
            return;
        }

        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            setMessages((prev) =>
                prev.concat({
                    id: makeId("a"),
                    isUser: false,
                    text: "Not connected to chat service. Reconnecting…",
                    timestamp: Date.now(),
                    agentName: "AI Assistant",
                })
            );
            scheduleReconnect();
            return;
        }

        setIsSending(true);
        setStreamStatus("");

        setMessages((prev) =>
            prev.concat({
                id: makeId("u"),
                isUser: true,
                text,
                timestamp: Date.now(),
            })
        );
        setInput("");

        ensureStreamingAssistant();

        ws.send(
            JSON.stringify({
                action: "sendMessage",
                reportId,
                sessionId,
                inputText: text,
            })
        );
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    }

    function reset() {
        clearHistory(sessionId);
        setMessages([]);
    }

    function scrollToBottom(behavior: ScrollBehavior = "auto") {
        const el = listRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }

    React.useEffect(() => {
        if (!open) return;
        requestAnimationFrame(() => scrollToBottom("auto"));
    }, [open]);

    if (!permissionLoaded || !hasAiChatbotPermission) {
        return null;
    }

    return (
        <React.Fragment>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className={"chatbot-toggle" + (isSending ? " chatbot-toggle--busy" : "")}
                    aria-label="Open chat"
                    title="Open chat"
                >
                    {showIconOnToggle !== false && hasCustomIcon ? renderIcon(Math.max(iconSize, 24)) : "Chat"}
                    {isSending && <span className="chatbot-toggle-indicator" aria-hidden="true" />}
                </button>
            )}

            {open && (
                <div className="chatbot-panel" role="dialog" aria-label="Chatbot">
                    <div className="chatbot-header">
                        <div className="chatbot-header-left">
                            {showIconInHeader !== false && hasCustomIcon && (
                                <span className="chatbot-header-icon">{renderIcon(iconSize)}</span>
                            )}
                            <strong>{title}</strong>
                            <span
                                className={
                                    "chatbot-status-dot " +
                                    (connStatus === "Connected"
                                        ? "connected"
                                        : connStatus === "Connecting"
                                            ? "connecting"
                                            : "disconnected")
                                }
                                aria-label={`${connStatus}`}
                                title={`${connStatus}`}
                            />
                        </div>
                        <div className="chatbot-header-right">
                            <button onClick={reset} className="chatbot-icon-btn" title="Clear chat" aria-label="Clear chat" disabled={isSending}>
                                ↺
                            </button>
                            <button onClick={() => setOpen(false)} className="chatbot-icon-btn" title="Close" aria-label="Close">
                                ✕
                            </button>
                        </div>
                    </div>

                    <div ref={listRef} className="chatbot-messages">
                        {messages.map((m) => (
                            <div
                                key={m.id}
                                className={"chatbot-bubble " + (m.isUser ? "chatbot-bubble--me" : "chatbot-bubble--bot")}
                            >
                                <div className="chatbot-meta">
                                    <strong>{m.isUser ? "You" : m.agentName || "AI Assistant"}</strong>
                                    <span>{new Date(m.timestamp).toLocaleString()}</span>
                                </div>
                                <div>
                                    {!m.isUser ? <div dangerouslySetInnerHTML={{ __html: m.text || "" }} /> : <span>{m.text}</span>}
                                </div>
                                {!m.isUser && m.isStreaming && (
                                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
                                        {streamStatus ? streamStatus : "Thinking…"}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="chatbot-disclaimer" role="note" aria-live="polite">
                        AI can make mistakes. Please validate important information.
                    </div>

                    <div className="chatbot-input-row">
                        <textarea
                            className={
                                "chatbot-input" +
                                (isSending || !wsConnected ? " chatbot-input--disabled" : "") +
                                (!wsConnected ? " chatbot-input--offline" : "")
                            }
                            placeholder={!wsConnected ? "Connecting to chat service…" : "Ask a question..."}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            rows={2}
                            disabled={isSending || !wsConnected}
                            aria-disabled={isSending || !wsConnected}
                        />
                        <button
                            className={
                                "chatbot-send-btn" +
                                (isSending ? " chatbot-send-btn--loading" : "") +
                                (!wsConnected ? " chatbot-send-btn--offline" : "")
                            }
                            onClick={send}
                            disabled={isSending || !(input || "").trim() || !wsConnected}
                            aria-busy={isSending}
                            aria-disabled={!wsConnected || isSending}
                            title={!wsConnected ? "Not connected" : "Send"}
                        >
                            {isSending ? (
                                <span className="btn-spinner-wrap">
                                    <span className="btn-spinner" aria-hidden="true" />
                                </span>
                            ) : (
                                "Send"
                            )}
                        </button>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default ChatbotFloatingWidget;